import cx from "classnames"
import PropTypes from "prop-types"
import React from "react"
import Button from "components/ui/Button"
import Image from "components/ui/Image"

function CTA({
  background,
  label,
  title,
  description,
  linkText,
  linkUrl,
  onButtonClick,
  dark,
  size = "regular",
  svgBackground,
  trackClick,
  children,
  splitLayout,
}) {
  // Text content (label, title, description)
  const textContent = (
    <>
      {label && (
        <div
          className={cx(
            "mb-3 text-xl font-bold",
            dark ? "text-seafoam" : "text-azure"
          )}
        >
          {label}
        </div>
      )}
      {title && (
        <h3
          className={cx(
            "text-3xl font-bold",
            dark ? "text-white" : "text-black"
          )}
        >
          {title}
        </h3>
      )}
      {description && (
        <p
          className={cx("mt-3 text-2xl", dark ? "text-sage" : "text-dark-grey")}
        >
          {description}
        </p>
      )}
    </>
  )

  // Button or children content
  const actionContent =
    children ||
    (linkText && (onButtonClick || linkUrl) && (
      <Button
        className="mt-5"
        href={linkUrl}
        onClick={onButtonClick}
        color={dark ? "catskill-white" : "lush"}
        size="medium"
        shadow={!dark}
        trackClick={trackClick}
      >
        {linkText}
      </Button>
    ))

  return (
    <div
      className={cx(
        "safari-overflow-fix relative overflow-hidden rounded-2x px-6 pt-14",
        dark ? "bg-big-stone" : "bg-drift-grey",
        {
          "pb-16 md:px-10": size === "regular",
          "py-10 md:py-16 lg:px-12 xl:px-20": size === "wide",
        }
      )}
    >
      {splitLayout && children ? (
        // Split layout: text on left, form on right
        <div className="relative z-10 grid gap-8 lg:grid-cols-2 lg:gap-12">
          <div>{textContent}</div>
          <div>{actionContent}</div>
        </div>
      ) : (
        // Default stacked layout
        <div className="relative z-10 max-w-xl">
          {textContent}
          {actionContent && <div className="mt-5">{actionContent}</div>}
        </div>
      )}
      {background &&
        (svgBackground ? (
          <div className="absolute top-0 left-0 h-full w-full">
            <Image
              className="h-full w-full object-contain object-right-bottom"
              src={background}
            />
          </div>
        ) : (
          <div className="absolute top-0 left-0 hidden h-full w-full xl:block">
            <Image className="h-full w-full" src={background} />
          </div>
        ))}
    </div>
  )
}

CTA.propTypes = {
  /** Background image for the CTA container */
  background: PropTypes.shape({}),
  /** Small text that appears above the title */
  label: PropTypes.string,
  /** Title */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Description */
  description: PropTypes.string,
  /** Button text */
  linkText: PropTypes.string,
  /** Button URL */
  linkUrl: PropTypes.string,
  /** Click handler (overrides linkUrl when provided) */
  onButtonClick: PropTypes.func,
  /** Sets spacing inside CTA container */
  size: PropTypes.oneOf(["regular", "wide"]),
  /** Track click event - if set, will track click */
  trackClick: PropTypes.string,
  /** If true, change text to light colors */
  dark: PropTypes.bool,
  /** If true, change background positioning */
  svgBackground: PropTypes.bool,
  /** Optional children to render instead of button (e.g., a form) */
  children: PropTypes.node,
  /** If true, use split layout with text on left, children on right */
  splitLayout: PropTypes.bool,
}

export default CTA
